import { assert, objectKeys, throwCaptured } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../../domain'

/**
 * This is the configuration of the mapping of all topics.
 */
const topicsMappingConfiguration = {
    Mapping: {
        /**
         * Topics may need to be stripped of their non MQTT standard markups
         *
         * Mappings already have their suffix, so there is no need to do anything except drop contextual information
         *
         * The topics from a mapping may come with 'foo/+bar/other/#bar'
         * And it needs to be converted to 'foo/+/other/#' so it becomes a valid mqtt topic
         *
         * The pattern below matches any MQTT topic level that starts with # or + sign, omitting from the level the non matched part
         * Turning '+foo' into '+'
         */
        replacement: '(\\+|#)[^/]+',

        /** There is no suffixing that needs to happen */
        suffixes: [],
    },
    Node: {
        suffixes: [
            { operation: null, mode: 'in', level: null },
            { operation: null, mode: 'out', level: null },

            { operation: 'serverReceives', mode: 'in', level: 'read' },

            { operation: 'serverProvides', mode: 'out', level: 'write' },

            { operation: 'serverProvidesAndReceives', mode: 'in', level: 'read' },
            { operation: 'serverProvidesAndReceives', mode: 'out', level: 'write' },

            { operation: 'transaction', mode: 'in', level: 'treq' },
            { operation: 'transaction', mode: 'out', level: 'tres' },
        ],
    },
    Endpoint: {
        suffixes: [
            { operation: 'subscribe', mode: 'in', level: null },

            { operation: 'write', mode: 'in', level: 'set' },
            { operation: 'write', mode: 'out', level: 'res' },

            { operation: 'read', mode: 'in', level: 'req' },
            { operation: 'read', mode: 'out', level: 'res' },
        ],
    },
} as const

type Configuration = typeof topicsMappingConfiguration

/**
 * The top level resource categories
 * These are the supported resources that this mapper can deal with
 *
 * Want to expand it? Go ahead
 * @see {Configuration}
 */
type TopicsCategories = keyof Configuration

type ExtractMatchingSuffixes<Category extends TopicsCategories, Operation, Mode> =
    /**
     * Extracts from the configuration a subset of matching suffixes depending on the given Category, Operation and Mode
     */
    Extract<Configuration[Category]['suffixes'][number], Readonly<{ operation: Operation, mode: Mode }>>

/**
 * Extracts from the given categories the expected operations
 * @example const operations: Operations<'Node'>[] = ['serverReceives', 'serverProvides', 'serverProvidesAndReceives', 'transaction', null]
 */
type Operations<Category extends TopicsCategories> = ExtractMatchingSuffixes<Category, unknown, unknown>['operation']

/**
 * Extracts from the given categories and operations combo the expected modes
 * @example const mode: Modes<'Node', 'serverProvides'> = 'out'
 */
type Modes<Category extends TopicsCategories, Operation extends Operations<Category>> = ExtractMatchingSuffixes<Category, Operation, unknown>['mode']

/**
 * Utility class to convert topics that reside in the configuration of resources (See supported resources below)
 * to topics that can be used in the Connectware ecosystem as MQTT topics
 *
 * Want to slightly modify a behaviour?
 * @see {Configuration}
 */
export class TopicsMapper<Category extends TopicsCategories, Operation extends Operations<Category> = never, Mode extends Modes<Category, Operation> = never> {
    static getSupportedCategories (): readonly TopicsCategories[] {
        return objectKeys(topicsMappingConfiguration)
    }

    static create<Category extends TopicsCategories> (category: Category): TopicsMapper<Category, Operations<Category>, Modes<Category, Operations<Category>>> {
        return new TopicsMapper(category, undefined, undefined)
    }

    private constructor (private readonly category: Category, private readonly operation: Operation | undefined, private readonly mode: Mode | undefined) {}

    private get configuration (): Configuration[Category] {
        return topicsMappingConfiguration[this.category]
    }

    private processTopic (topic: string): string {
        return 'replacement' in this.configuration ? topic.replaceAll(new RegExp(this.configuration.replacement, 'g'), '$1') : topic
    }

    /**
     * Yields the given array while ensuring it has not exceeded the max size
     */
    private createTopicsWithMaxSize (topics: readonly string[], maxSize: number): readonly string[] {
        assert(
            topics.length <= maxSize,
            /**
             * Yielded topics are already over the expected limit
             */
            new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Unexpected amount of topics was mapped', {
                category: this.category,
                operation: this.operation,
                mode: this.mode,
                expectedSize: maxSize,
                topics,
            })
        )

        return topics
    }

    private createTopicsWithSuffixes (topic: string, maxSize: number): readonly string[] {
        let out: readonly string[] = []

        for (const { operation, mode, level } of this.configuration.suffixes) {
            if ((this.operation !== undefined && this.operation !== operation) || (this.mode !== undefined && this.mode !== mode)) {
                /** Not a match, so move on */
                continue
            }

            /** Null level, means no suffix to add */
            const finalTopic = level === null ? topic : `${topic}/${level}`

            if (!out.includes(finalTopic)) {
                out = this.createTopicsWithMaxSize([...out, finalTopic], maxSize)
            }
        }

        return out
    }

    private mapTopics (topic: string, maxSize: number, capture: (topic: string) => unknown): readonly string[] {
        try {
            const processedTopic = this.processTopic(topic)

            return this.configuration.suffixes.length === 0
                ? this.createTopicsWithMaxSize([processedTopic], maxSize)
                : this.createTopicsWithSuffixes(processedTopic, maxSize)
        } catch (e: unknown) {
            return throwCaptured(e as ConnectwareError, capture)
        }
    }

    withOperation<Operation extends Operations<Category>> (operation: Operation): TopicsMapper<Category, Operation, Modes<Category, Operation>> {
        return new TopicsMapper(this.category, operation, undefined)
    }

    withMode<Mode extends Modes<Category, Operation>> (mode: Mode): TopicsMapper<Category, Operation, Mode> {
        return new TopicsMapper(this.category, this.operation, mode)
    }

    mapAll (topic: string): readonly string[] {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        return this.mapTopics(topic, Infinity, this.mapAll)
    }

    /**
     * @throws if more than or less than one topic was yielded
     */
    mapSingle (topic: string): string {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const [firstTopic] = this.mapTopics(topic, 1, this.mapSingle) as readonly [string]
        return firstTopic
    }
}
